import React from 'react';
import { useTranslation } from 'react-i18next';
import { SEO } from './SEO';
import {
  CheckCircle as CheckCircleIcon,
  Lightbulb as LightbulbIcon,
  Translate as TranslateIcon,
  School as SchoolIcon,
  Business as BusinessIcon,
  WhatsApp as WhatsAppIcon,
  AutoAwesome,
  Psychology,
  FilterAlt,
  TouchApp,
  QuestionAnswer,
  AllInclusive,
  Explore,
  Tune,
  WbSunny,
  LibraryBooks,
  TouchAppOutlined
} from '@mui/icons-material';

const SarvalokFeatures: React.FC = () => {
  const { t } = useTranslation('features');
  
  return (
    <>
      <SEO 
        title={t('seo:features.title', { defaultValue: 'Features | Sarvalok AI Capabilities' })}
        description={t('seo:features.description', { defaultValue: "Explore Sarvalok's powerful features for cultural research, authentic source citations, multilingual support, and AI-powered analysis of ancient texts." })}
        url="/features"
      />
      <div className="sl-features-page relative overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 text-black dark:text-white px-8 lg:px-20 py-12">
      <div
        aria-hidden="true"
        className="sl-features-kalash-ornament pointer-events-none absolute right-4 top-0 hidden lg:block xl:right-8"
      >
        <img
          src="/kalashsmall.jpg"
          alt=""
          className="h-[191px] w-[161px] object-contain xl:h-[224px] xl:w-[187px]"
        />
      </div>
      {/* Header Section */}
      <div className="text-center mb-16">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-4">
          <h1 className="text-5xl font-bold mb-4">{t('features.header_title')}</h1>
        </div>
        <h2 className="text-3xl font-semibold mb-6 text-gray-800 dark:text-white">{t('features.header_subtitle')}</h2>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed">
          {t('features.header_description')}
        </p>
      </div>

      {/* Core Features Grid */}
      <div className="mb-20">
        <h2 className="text-3xl font-bold text-center mb-12 text-gray-800 dark:text-white">{t('features.core_features_title')}</h2>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
        {/* Feature 1 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-green-500 to-emerald-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <CheckCircleIcon className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.source_grounded_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('features.source_grounded_description')}
          </p>
        </div>

        {/* Feature 2 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-blue-500 to-cyan-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <AutoAwesome className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.ai_powered_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('features.ai_powered_description')}
          </p>
        </div>

        {/* Feature 3 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-purple-500 to-pink-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <FilterAlt className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.authority_filter_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('features.authority_filter_description')}
          </p>
        </div>

        {/* Feature 4 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-orange-500 to-red-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <TouchApp className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.interactive_terms_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('features.interactive_terms_description')}
          </p>
        </div>

        {/* Feature 5 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-teal-500 to-green-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <QuestionAnswer className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.related_questions_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('features.related_questions_description')}
          </p>
        </div>

        {/* Feature 6 */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-indigo-500 to-purple-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <AllInclusive className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.ask_anything_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed mb-4">
            {t('features.ask_anything_description')}
          </p>
          <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-2">
            <li className="flex items-start"><span className="text-indigo-500 mr-2">•</span>{t('features.ask_anything_item1')}</li>
            <li className="flex items-start"><span className="text-indigo-500 mr-2">•</span>{t('features.ask_anything_item2')}</li>
            <li className="flex items-start"><span className="text-indigo-500 mr-2">•</span>{t('features.ask_anything_item3')}</li>
            <li className="flex items-start"><span className="text-indigo-500 mr-2">•</span>{t('features.ask_anything_item4')}</li>
          </ul>
        </div>
      </div>
      </div>

      {/* Subject Areas Section */}
      <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-12 mb-16 text-white">
        <h3 className="text-3xl font-bold mb-8 text-center">{t('features.subject_areas_title')}</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
             <BusinessIcon className="text-3xl mb-4" />
             <h4 className="font-semibold mb-2">{t('features.economics_governance_title')}</h4>
             <p className="text-sm opacity-90">{t('features.economics_governance_description')}</p>
           </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
            <Psychology className="text-3xl mb-4" />
            <h4 className="font-semibold mb-2">{t('features.science_technology_title')}</h4>
            <p className="text-sm opacity-90">{t('features.science_technology_description')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
            <WbSunny className="text-3xl mb-4" />
            <h4 className="font-semibold mb-2">{t('features.yoga_wellness_title')}</h4>
            <p className="text-sm opacity-90">{t('features.yoga_wellness_description')}</p>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
             <SchoolIcon className="text-3xl mb-4" />
             <h4 className="font-semibold mb-2">{t('features.social_ethics_title')}</h4>
             <p className="text-sm opacity-90">{t('features.social_ethics_description')}</p>
           </div>
           <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
             <LightbulbIcon className="text-3xl mb-4" />
             <h4 className="font-semibold mb-2">{t('features.mathematics_architecture_title')}</h4>
             <p className="text-sm opacity-90">{t('features.mathematics_architecture_description')}</p>
           </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
            <LibraryBooks className="text-3xl mb-4" />
            <h4 className="font-semibold mb-2">{t('features.literature_arts_title')}</h4>
            <p className="text-sm opacity-90">{t('features.literature_arts_description')}</p>
          </div>
        </div>
      </div>

      {/* Additional Features */}
      <div className="grid md:grid-cols-2 gap-8 mb-16">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-cyan-500 to-blue-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <Tune className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.customizable_experience_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('features.customizable_experience_description')}
          </p>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg p-8 hover:shadow-xl transition-all duration-300 border border-gray-100 dark:border-gray-600">
          <div className="bg-gradient-to-r from-yellow-500 to-orange-500 w-16 h-16 rounded-full flex items-center justify-center mb-6">
            <WbSunny className="text-white text-2xl" />
          </div>
          <h3 className="text-xl font-semibold mb-4 text-gray-800 dark:text-white">{t('features.timeless_wisdom_title')}</h3>
          <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('features.timeless_wisdom_description')}
          </p>
        </div>
      </div>

      {/* Final CTA Section */}
      <div className="sl-features-final-cta text-center bg-gradient-to-r from-gray-50 to-blue-50 dark:from-gray-800 dark:to-gray-700 rounded-2xl p-12">
        <h3 className="text-3xl font-bold mb-6 bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
          {t('features.final_cta_title')}
        </h3>
        <p className="text-lg text-gray-600 dark:text-gray-300 max-w-4xl mx-auto leading-relaxed mb-6">
          {t('features.final_cta_description')}
        </p>
        <p className="text-gray-600 dark:text-gray-300 max-w-3xl mx-auto">
          {t('features.final_cta_subtitle')}
        </p>
      </div>
    </div>
    </>
  );
};

export default SarvalokFeatures;
